import c from 'classnames';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { urls } from '@repo/app-constants';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import useSignOut from 'services/auth/useSignOut';
import useFeature from 'services/features/useFeature';
import useSubscriptions from 'services/general/subscriptionsList';
import { getAccountEmail, getFullName } from 'store/account';
import { getIsImpersonatorPartnerOrPractitioner } from 'store/user';

import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  LinkButton,
  Typography,
} from 'components';
import AccountEmailModal from './AccountEmailModal';
import AccountField from './AccountField/AccountField';
import AccountNameModal from './AccountNameModal';
import {
  ACCOUNT_DELETION_REQUEST_FLAG,
  DELETE_ACCOUNT_PATH,
} from './DeleteAccount/DeleteAccount';
import { Practitioners } from './Practitioners/Practitioners';

import styles from './Account.module.css';

const Account = () => {
  const navigate = useNavigate();
  const signOut = useSignOut();
  const { trackEvent } = useAnalytics();
  const fullName = useSelector(getFullName);
  const email = useSelector(getAccountEmail);
  const [showModalName, setShowModalName] = useState<boolean>(false);
  const [showModalEmail, setShowModalEmail] = useState<boolean>(false);
  const { isAnyTinyPlus, isTinyPlus } = useSubscriptions();
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs();
  const { isFeatureEnabled: isAccountDeletionEnabled } = useFeature(
    ACCOUNT_DELETION_REQUEST_FLAG,
  );
  const isImpersonatorPartnerOrPractitioner = useSelector(
    getIsImpersonatorPartnerOrPractitioner,
  );

  useEffect(() => {
    trackEvent('Account Page Viewed');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editName = () => {
    setShowModalName(true);
  };
  const editEmail = () => {
    setShowModalEmail(true);
  };

  const viewVersionLog = () => {
    window.open(urls.VERSION_LOG, '_blank');
  };
  const viewSubscriptions = () => {
    trackEvent('Manage Membership Clicked', { source: 'account' });
    navigate(`/account/subscriptions`);
  };
  const toggleModalName = () => setShowModalName(prev => !prev);
  const toggleModalEmail = () => setShowModalEmail(prev => !prev);

  return (
    <>
      {fullName && (
        <AccountNameModal toggle={toggleModalName} showModal={showModalName} />
      )}
      {email && (
        <AccountEmailModal
          toggle={toggleModalEmail}
          showModal={showModalEmail}
        />
      )}
      <div className={styles.accountContainer}>
        <Typography variant='heading-xl' className='mb-4'>
          Account
        </Typography>
        <div className={styles.section}>
          <AccountField title='Name' action={editName} actionName='Edit'>
            <Typography variant='body-s' className={styles.value}>
              {fullName}
            </Typography>
          </AccountField>
          <AccountField title='Email' action={editEmail} actionName='Edit'>
            <Typography variant='body-s' className={styles.value}>
              {email}
            </Typography>
          </AccountField>
        </div>

        {/* A non-member whose portal config hides upsells has nothing to see
            here: the section would only be the Tiny+ pitch (ENG-5047). */}
        {(isAnyTinyPlus ||
          (!portalConfig.hide_upsells && !isPortalConfigLoading)) && (
          <div className={styles.section}>
            <AccountField
              title={'Membership'}
              action={isAnyTinyPlus ? viewSubscriptions : undefined}
              actionName={isAnyTinyPlus ? 'Manage' : ''}
            >
              {isAnyTinyPlus ? (
                <div className='flex flex-column'>
                  <Typography variant='body-s' className={styles.value}>
                    Tiny+ member
                  </Typography>
                  {!portalConfig.hide_upsells && !isPortalConfigLoading && (
                    <Typography
                      variant='body-s'
                      className={c(styles.value, 'mt-2')}
                    >
                      {`Access your Tiny+ members-only rate of $${
                        isTinyPlus ? 149 : 169
                      } per kit: `}
                      <LinkButton
                        label='Gut kit'
                        href={
                          isTinyPlus
                            ? urls.CHECKOUT_WITH_KIT_TINY_PLUS
                            : urls.CHECKOUT_WITH_KIT_TINY_PLUS_PROGRAM_OR_PROACTIVE +
                              '&utm_source=tinyhealth&utm_medium=webapp&utm_campaign=accountpage'
                        }
                        external
                        variant={ButtonVariants.TEXT}
                        size={ButtonSizes.M}
                        color={ButtonColors.PURPLE}
                      />
                      {`, `}
                      <LinkButton
                        label='Vaginal kit.'
                        href={
                          isTinyPlus
                            ? urls.CHECKOUT_WITH_KIT_TINY_PLUS_VAGINAL
                            : urls.CHECKOUT_WITH_KIT_TINY_PLUS_PROGRAM_OR_PROACTIVE_VAGINAL +
                              '&utm_source=tinyhealth&utm_medium=webapp&utm_campaign=accountpage'
                        }
                        external
                        variant={ButtonVariants.TEXT}
                        size={ButtonSizes.M}
                        color={ButtonColors.PURPLE}
                      />
                    </Typography>
                  )}
                </div>
              ) : (
                <div className='flex flex-column'>
                  <Typography variant='body-s' className={styles.value}>
                    Basic
                  </Typography>
                  <LinkButton
                    label='Learn more about Tiny+ memberships'
                    href={urls.TINY_PLUS_UPGRADE_OFFER}
                    external
                    variant={ButtonVariants.TEXT}
                    size={ButtonSizes.M}
                    color={ButtonColors.PURPLE}
                  />
                </div>
              )}
            </AccountField>
          </div>
        )}

        <div className={styles.section}>
          <AccountField title='Practitioner'>
            <div className='flex flex-column'>
              <Typography variant='body-s' className={styles.value}>
                {`If your practitioner is a Tiny Health Member you can add them here, This will give them access to your results, surveys and action plan.`}
              </Typography>
              <Practitioners />
            </div>
          </AccountField>
        </div>
        <div className={styles.section}>
          <AccountField
            title='Version Log'
            action={viewVersionLog}
            actionName={'View'}
          >
            <Typography variant='body-s' className={styles.value}>
              Email hello@tinyhealth.com to update to the latest system version.
            </Typography>
          </AccountField>
        </div>
        <div className={styles.actions}>
          <Button
            label='Sign Out'
            size={ButtonSizes.M}
            variant={'outline'}
            width={'full'}
            iconNameLeft='logOut'
            onClick={signOut}
          />
          {isAccountDeletionEnabled && !isImpersonatorPartnerOrPractitioner && (
            <Button
              label='Delete my account'
              size={ButtonSizes.M}
              variant={'outline'}
              color={ButtonColors.RED}
              width={'full'}
              onClick={() => navigate(DELETE_ACCOUNT_PATH)}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Account;
